import React, { useEffect, useState, Suspense } from "react";
import { Modal, Tab, Tabs } from "@mui/material";
import { styled } from "@mui/material/styles";
import { COMPONENT_MODES, CTA_TYPE, CTA_SIZE } from "../../../data/constants";
import CtaComponent from "../../../components/reusable-ui/cta";
import { validateItemData } from "../../../helpers/item-helper";
import ChargesDetailsEditView from "./charges-details";
import ChannelsDetailsEditView from "./channels-details";
import { RootState } from "../../../redux/store";
import { useSelector } from "react-redux";
import ChannelsManagementComponent from "./channnels-management";

interface Props {
  data: any;
  chargeId: string;
  updateChargeDataInParentState?: any;
  onDiscardCtaclick?: any;
  onSaveCtaClick?: any;
  errorMessageMap?: any;
  isChangesMade?: boolean;
  modeConfig?: any;
  setModeConfig?: any;
}

const AntTabs = styled(Tabs)({
  borderBottom: "1px solid #e8e8e8",
  "& .MuiTabs-indicator": {
    backgroundColor: "#000000",
    height: "4px",
  },
});

const ChargesEditView = (props: Props) => {
  const {
    data,
    modeConfig,
    chargeId,
    updateChargeDataInParentState,
    onDiscardCtaclick,
    onSaveCtaClick,
    setModeConfig,
    errorMessageMap,
    isChangesMade,
  } = props;
  const CHARGE_DETAILS_TABS: any = [
    { id: "details", text: "Details" },
    { id: "channels", text: "Channels" },
  ];
  const [selectedTab, setSelectedTab] = useState<string>(
    CHARGE_DETAILS_TABS[0].id
  );
  const chargesDataMap = useSelector(
    (state: RootState) => state.chargesAndDiscountsReducer.chargesDataMap
  );

  useEffect(() => {
    setSelectedTab("details");
  }, [chargeId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: any) => {
    setSelectedTab(newValue);
  };

  const commonProps = {
    data,
    modeConfig,
    setModeConfig,
    updateChargeDataInParentState,
    errorMessageMap,
    onSaveCtaClick,
  };

  function renderHeaderTabs(): any {
    return modeConfig.type === COMPONENT_MODES.editMode ? null : (
      <AntTabs
        value={selectedTab}
        onChange={handleTabChange}
        aria-label="catalogue-tab"
        className="px-24 sm:left-[0px] z-[11] w-full border-b-[1px] border-[#F0F0F0] mb-[6px]"
      >
        {CHARGE_DETAILS_TABS.map((item: { text: string; id: string }) => (
          <Tab
            label={item.text}
            key={item.id}
            value={item.id}
            className={`${
              item.id === selectedTab
                ? "font-semibold"
                : "font-normal opacity-60"
            } text-[#000] capitalize text-14`}
          />
        ))}
      </AntTabs>
    );
  }

  function renderSelectedTab(): React.JSX.Element {
    switch (selectedTab) {
      case "details":
        return (
          <Suspense fallback={""}>
            <ChargesDetailsEditView {...commonProps} />
          </Suspense>
        );
      case "channels":
        return (
          <Suspense fallback={""}>
            <ChannelsDetailsEditView {...commonProps} />
          </Suspense>
        );
      default:
        return (
          <Suspense fallback={""}>
            <ChargesDetailsEditView {...commonProps} />
          </Suspense>
        );
    }
  }

  const renderChannelEditModal = () => {
    return (
      <Modal open={true} onClose={onDiscardCtaclick}>
        <div className="absolute w-[636px] h-[668px] rounded-[8px] overflow-hidden left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white">
          <div className="flex items-center justify-between pt-[16px] pl-[24px] pr-[12px] pb-[36px]">
            <h1 className="text-[22px] font-semibold">Edit Channels</h1>
            <div
              className="rista-sprite ic-cross-black"
              onClick={onDiscardCtaclick}
            ></div>
          </div>
          <div className="w-full h-[506px] overflow-y-auto px-[24px]">
            <ChannelsManagementComponent {...commonProps} />
          </div>
          <div className="absolute bottom-[0px] w-full z-1 right-0 bg-white h-[70px] px-24 flex justify-end border-t border-[#EBEBEB] items-center overflow-hidden sm:w-full">
            <CtaComponent
              ctaText={"Discard"}
              ctaType={CTA_TYPE.secondary_cta}
              ctaSize={CTA_SIZE.medium}
              onCtaClick={onDiscardCtaclick}
              customClass={"w-[96px] mr-10"}
            ></CtaComponent>
            <CtaComponent
              ctaText={"Save Changes"}
              ctaType={CTA_TYPE.active_cta}
              ctaSize={CTA_SIZE.medium}
              onCtaClick={onSaveCtaClick}
              customClass={"w-[160px] ml-10"}
              // isDisabled={!isChangesMade}
            ></CtaComponent>
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <section className="w-[50%]">
      <div className="text-[20px] font-normal p-[24px] pb-0 capitalize">
        {chargesDataMap[chargeId]?.name}
      </div>
      {renderHeaderTabs()}
      <div className="p-[24px] pt-[18px] h-[calc(100vh-250px)] overflow-y-auto">
        {chargesDataMap[chargeId] &&
          !!Object.keys(chargesDataMap[chargeId])?.length &&
          renderSelectedTab()}
      </div>
      {modeConfig.type === COMPONENT_MODES.editMode &&
        selectedTab === "details" && (
          <div className="fixed bottom-[0px] right-0 z-[111] bg-white h-[70px] px-24 flex justify-center items-center overflow-hidden w-[calc(50%-48px)] sm:w-full border-t-[1px] border-[#f5f5f5]">
            <CtaComponent
              ctaText={"Discard"}
              ctaType={CTA_TYPE.secondary_cta}
              ctaSize={CTA_SIZE.medium}
              onCtaClick={onDiscardCtaclick}
              customClass={"w-[calc(50%-10px)] mr-10"}
            ></CtaComponent>
            <CtaComponent
              ctaText={"Save Changes"}
              ctaType={CTA_TYPE.active_cta}
              ctaSize={CTA_SIZE.medium}
              onCtaClick={onSaveCtaClick}
              customClass={"w-[calc(50%-10px)] ml-10"}
              isDisabled={!isChangesMade}
            ></CtaComponent>
          </div>
        )}

      {modeConfig.type === COMPONENT_MODES.editMode &&
        selectedTab === "channels" &&
        renderChannelEditModal()}
    </section>
  );
};

export default ChargesEditView;
